export interface PaymentRules {
  canInstallment: boolean;
  minEntryPercentage: number;
  maxInstallments: number;
  availablePaymentMethods: Array<{ value: string; label: string }>;
  fees: {
    rate: number;
    fixed: number;
    description: string;
  };
  minEntryValue: number;
  requiresEntry: boolean;
  minInstallmentValue: number;
}

export const calculatePaymentRules = (totalValue: number, paymentMethod?: string): PaymentRules => {
  const base: PaymentRules = {
    canInstallment: false,
    minEntryPercentage: 0,
    maxInstallments: 1,
    availablePaymentMethods: [],
    fees: { rate: 0, fixed: 0, description: '' },
    minEntryValue: 50,
    requiresEntry: true,
    minInstallmentValue: 50
  };

  switch (paymentMethod) {
    case 'CREDIT_CARD':
      return {
        ...base,
        canInstallment: totalValue >= 250,
        maxInstallments: totalValue >= 250 ? 12 : 1,
        requiresEntry: false,
        fees: getCreditCardFees(1),
        availablePaymentMethods: [{ value: "CREDIT_CARD", label: "Cartão de Crédito" }],
        minInstallmentValue: 0,
        minEntryPercentage: 0
      };

    case 'PIX':
    case 'BOLETO': {
      const maxInstallments = calculateMaxInstallmentsByMinValue(totalValue);
      return {
        ...base,
        canInstallment: maxInstallments > 1,
        maxInstallments,
        minEntryPercentage: getBoletoPixMinEntry(totalValue),
        fees: { rate: 0, fixed: 0, description: 'Sem taxa' },
        availablePaymentMethods: [
          { value: paymentMethod, label: paymentMethod === 'PIX' ? 'PIX' : 'Boleto' }
        ]
      };
    }

    default:
      return base;
  }
};


const getBoletoPixMinEntry = (totalValue: number): number => {

  if (totalValue < 50) return 100;
  if (totalValue <= 100) return 100;
  if (totalValue <= 600) return 70;
  if (totalValue >= 1000 && totalValue <= 1500) return 50;
  if (totalValue >= 1501) return 30;
  return 70;
};

const calculateMaxInstallmentsByMinValue = (totalValue: number): number => {

  if (totalValue < 50) {
    return 1;
  }

  if (totalValue <= 100) {
    return 1;
  }

  const minEntryPercentage = getBoletoPixMinEntry(totalValue);
  const entryValue = Math.max((minEntryPercentage / 100) * totalValue, 50);
  const remainingValue = totalValue - entryValue;

  if (remainingValue < 50) {
    return 1;
  }

  const maxPossibleInstallments = Math.floor(remainingValue / 50);

  const finalMaxInstallments = Math.min(maxPossibleInstallments, 15);

  return finalMaxInstallments;
};


export const getCreditCardFees = (installments: number) => {
  if (installments === 1) {
    return { rate: 1.45, fixed: 0.29, description: "1,45% + R$ 0,29 à vista" };
  }
  if (installments >= 2 && installments <= 6) {
    return { rate: 2.49, fixed: 0.29, description: "2,49% + R$ 0,29 (2 a 6 parcelas)" };
  }
  if (installments >= 7 && installments <= 10) {
    return { rate: 2.99, fixed: 0.29, description: "2,99% + R$ 0,29 (7 a 10 parcelas)" };
  }
  if (installments >= 11 && installments <= 12) {
    return { rate: 4.29, fixed: 0.29, description: "4,29% + R$ 0,29 (11 a 12 parcelas)" };
  }
  return { rate: 1.45, fixed: 0.29, description: "1,45% + R$ 0,29" };
};


export const calculateCreditCardInstallmentValue = (totalValue: number, installments: number): number => {
  if (installments <= 1) {

    const fees = getCreditCardFees(1);
    return totalValue * (1 + fees.rate / 100) + fees.fixed;
  }

  const fees = getCreditCardFees(installments);
  const monthlyRate = fees.rate / 100;


  const r = monthlyRate;
  const n = installments;
  const pv = totalValue;

  const factor = (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const monthlyValueWithInterest = pv * factor;
  const monthlyValueWithFees = monthlyValueWithInterest + (fees.fixed / installments);

  return monthlyValueWithFees;
};

export const calculateCreditCardFinalValue = (totalValue: number, installments: number): number => {
  const monthlyValueWithFees = calculateCreditCardInstallmentValue(totalValue, installments);
  return monthlyValueWithFees * installments;
};

export const calculateMinEntryValue = (totalValue: number, percentage: number): number => {
  const calculatedEntry = (percentage / 100) * totalValue;
  return Math.max(calculatedEntry, 50);
};

export const validateInstallmentMinValue = (remainingValue: number, installments: number): boolean => {
  if (installments <= 1) return true;

  const installmentValue = remainingValue / installments;
  return installmentValue >= 50;
};

export const canValueBeInstalled = (totalValue: number, entryValue: number = 0): boolean => {

  if (totalValue < 50) return false;

  if (entryValue === 0) {
    const minEntryPercentage = getBoletoPixMinEntry(totalValue);
    entryValue = Math.max((minEntryPercentage / 100) * totalValue, 50);
  }

  const remainingValue = totalValue - entryValue;

  return remainingValue >= 50;
};